import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { FeatureProvider } from "./contexts/FeatureContext";
import { Toaster } from "sonner";
import LoginPage from "./pages/LoginPage";
import Dashboard from "./pages/Dashboard";
import ReceiptList from "./pages/ReceiptList";
import ReceiptForm from "./pages/ReceiptForm";
import ReceiptView from "./pages/ReceiptView";
import InvoiceList from "./pages/InvoiceList";
import InvoiceForm from "./pages/InvoiceForm";
import InvoiceView from "./pages/InvoiceView";
import LedgerPage from "./pages/LedgerPage";
import GSTReports from "./pages/GSTReports";
import EmployeeManagement from "./pages/EmployeeManagement";
import AuditLogPage from "./pages/AuditLogPage";
import Tutorial from "./pages/Tutorial";
import PartyManagement from "./pages/PartyManagement";
import ChartOfAccounts from "./pages/ChartOfAccounts";
import JournalEntryForm from "./pages/JournalEntryForm";
import JournalEntryList from "./pages/JournalEntryList";
import ProfessionalLedger from "./pages/ProfessionalLedger";
import ProfitLoss from "./pages/ProfitLoss";
import TrialBalance from "./pages/TrialBalance";
import ClientAnalytics from "./pages/ClientAnalytics";
import BalanceSheet from "./pages/BalanceSheet";
import CompanyProfile from "./pages/CompanyProfile";
import Masters from "./pages/Masters";
import FinancialControls from "./pages/FinancialControls";
import TrainingCenter from "./pages/TrainingCenter";
import SuperAdminPage from "./pages/SuperAdminPage";
import PlatformAdminPage from "./pages/PlatformAdminPage";
import LandingPage from "./pages/LandingPage";
import AIAssistant from "./pages/AIAssistant";
import ImportEngine from "./pages/ImportEngine";
import EmailTemplates from "./pages/EmailTemplates";
import AboutUs from "./pages/AboutUs";
import ContactUs from "./pages/ContactUs";
import BlogList from "./pages/BlogList";
import BlogPost from "./pages/BlogPost";
import WhatsAppWidget from "./components/WhatsAppWidget";
import FinancialYearClose from "./pages/FinancialYearClose";
import BankReconciliation from "./pages/BankReconciliation";
import GSTExport from "./pages/GSTExport";
import TDSModule from "./pages/TDSModule";
import PayrollModule from "./pages/PayrollModule";
import AssetDepreciation from "./pages/AssetDepreciation";
import AdvancedAnalytics from "./pages/AdvancedAnalytics";
import PartyAnalytics from "./pages/PartyAnalytics";
import AdvanceList from "./pages/AdvanceList";
import AdvanceForm from "./pages/AdvanceForm";
import AdvanceSummary from "./pages/AdvanceSummary";
import CollectionMemoList from "./pages/CollectionMemoList";
import CollectionMemoForm from "./pages/CollectionMemoForm";
import CollectionMemoView from "./pages/CollectionMemoView";
import TransactionsPage from "./pages/TransactionsPage";
import GSTReturnsPage from "./pages/GSTReturnsPage";
import TDSReturnsPage from "./pages/TDSReturnsPage";
import BankAccountsPage from "./pages/BankAccountsPage";
import PartyLedgerPage from "./pages/PartyLedgerPage";
import LedgerDashboard from "./pages/LedgerDashboard";
import Chatbot from "./components/Chatbot";
import Layout from "./components/Layout";
import BranchesPage from "./pages/BranchesPage";
import CustomReportBuilder from "./pages/CustomReportBuilder";
import VehiclesPage from "./pages/VehiclesPage";
import DriversPage from "./pages/DriversPage";
import VehicleView from "./pages/VehicleView";
import DriverView from "./pages/DriverView";
import BillBooksPage from "./pages/BillBooksPage";
import FleetReportsPage from "./pages/FleetReportsPage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center"><div className="w-5 h-5 border-2 border-[hsl(var(--foreground))] border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AuthAwareLayout() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center"><div className="w-5 h-5 border-2 border-[hsl(var(--foreground))] border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <LandingPage />;
  return <Layout />;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/blog" element={<BlogList />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/" element={<AuthAwareLayout />}>
        <Route index element={<Dashboard />} />
        {/* Accounting */}
        <Route path="transactions" element={<TransactionsPage />} />
        <Route path="voucher-entries" element={<JournalEntryList />} />
        <Route path="voucher-entries/new" element={<JournalEntryForm />} />
        <Route path="chart-of-accounts" element={<ChartOfAccounts />} />
        <Route path="ledger" element={<LedgerDashboard />} />
        <Route path="party-ledger" element={<LedgerDashboard />} />
        <Route path="account-ledger" element={<ProfessionalLedger />} />
        <Route path="trial-balance" element={<TrialBalance />} />
        <Route path="profit-loss" element={<ProfitLoss />} />
        <Route path="balance-sheet" element={<BalanceSheet />} />
        {/* Backward compat for old accounting paths */}
        <Route path="accounting/chart-of-accounts" element={<ChartOfAccounts />} />
        <Route path="accounting/journal-entries" element={<JournalEntryList />} />
        <Route path="accounting/journal-entries/new" element={<JournalEntryForm />} />
        <Route path="accounting/journal-entries/:entryId/edit" element={<JournalEntryForm />} />
        <Route path="accounting/ledger" element={<ProfessionalLedger />} />
        <Route path="accounting/profit-loss" element={<ProfitLoss />} />
        <Route path="accounting/trial-balance" element={<TrialBalance />} />
        <Route path="accounting/balance-sheet" element={<BalanceSheet />} />
        <Route path="accounting/client-analytics" element={<ClientAnalytics />} />
        {/* Sales */}
        <Route path="invoices" element={<InvoiceList />} />
        <Route path="invoices/new" element={<InvoiceForm />} />
        <Route path="invoices/:id/edit" element={<InvoiceForm />} />
        <Route path="invoices/:id" element={<InvoiceView />} />
        <Route path="advances" element={<AdvanceList />} />
        <Route path="advances/new" element={<AdvanceForm />} />
        <Route path="bank-accounts" element={<BankAccountsPage />} />
        {/* Transport */}
        <Route path="receipts" element={<ReceiptList />} />
        <Route path="receipts/new" element={<ReceiptForm />} />
        <Route path="receipts/:id/edit" element={<ReceiptForm />} />
        <Route path="receipts/:id" element={<ReceiptView />} />
        <Route path="collection-memos" element={<CollectionMemoList />} />
        <Route path="collection-memos/new" element={<CollectionMemoForm />} />
        <Route path="collection-memos/:id/edit" element={<CollectionMemoForm />} />
        <Route path="collection-memos/:id" element={<CollectionMemoView />} />
        <Route path="vehicles" element={<VehiclesPage />} />
        <Route path="vehicles/:id" element={<VehicleView />} />
        <Route path="drivers" element={<DriversPage />} />
        <Route path="drivers/:id" element={<DriverView />} />
        <Route path="bill-books" element={<BillBooksPage />} />
        <Route path="fleet-reports" element={<FleetReportsPage />} />
        <Route path="trips" element={<div className="p-6"><h1 className="font-heading font-bold text-2xl text-slate-900">Trips</h1><p className="text-sm text-slate-500 mt-2">Trip management coming soon</p></div>} />
        {/* Reports */}
        <Route path="gst-returns" element={<GSTReturnsPage />} />
        <Route path="tds-returns" element={<TDSReturnsPage />} />
        <Route path="party-analytics" element={<PartyAnalytics />} />
        <Route path="advance-summary" element={<AdvanceSummary />} />
        <Route path="gst-reports" element={<GSTReports />} />
        <Route path="gst-export" element={<GSTExport />} />
        {/* Masters */}
        <Route path="parties" element={<PartyManagement />} />
        <Route path="masters" element={<Masters />} />
        {/* Admin */}
        <Route path="employees" element={<EmployeeManagement />} />
        <Route path="settings" element={<CompanyProfile />} />
        <Route path="super-admin" element={<SuperAdminPage />} />
        <Route path="platform-admin" element={<PlatformAdminPage />} />
        <Route path="audit-logs" element={<AuditLogPage />} />
        {/* Other */}
        <Route path="ledger-old" element={<LedgerPage />} />
        <Route path="company-profile" element={<CompanyProfile />} />
        <Route path="tutorial" element={<Tutorial />} />
        <Route path="financial-controls" element={<FinancialControls />} />
        <Route path="training" element={<TrainingCenter />} />
        <Route path="ai-assistant" element={<AIAssistant />} />
        <Route path="import" element={<ImportEngine />} />
        <Route path="email-templates" element={<EmailTemplates />} />
        <Route path="financial-year" element={<FinancialYearClose />} />
        <Route path="bank-reconciliation" element={<BankReconciliation />} />
        <Route path="branches" element={<BranchesPage />} />
        <Route path="report-builder" element={<CustomReportBuilder />} />
        <Route path="tds" element={<TDSModule />} />
        <Route path="payroll" element={<PayrollModule />} />
        <Route path="assets" element={<AssetDepreciation />} />
        <Route path="analytics" element={<AdvancedAnalytics />} />
        <Route path="collection-memo" element={<CollectionMemoList />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <FeatureProvider>
            <AppRouter />
            <Chatbot />
          </FeatureProvider>
          <WhatsAppWidget />
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
